// ============================================================
// ClientCasesTab.tsx
// The client page's CASES tab — approved mockup casevo-client-cases-tab.html,
// Option B (compact client-tab cards). Same visual language as the caselist's
// forest card (CaseListScreen renderCaseCard): forest gradient (literal hex —
// must not flip in dark), gold "yours" bar + "You" chip, white status pill,
// Setup progress — laid out as 2-across tiles sized for the 870px tab column.
// No client name on the card (it's always this client).
//
// Presentational: ClientSearchView maps its rows (shared caseName()/caseRef(),
// getStatus, calculateCaseCompletion) into CaseCardModel and owns the actions —
// every card click opens the SAME case quick-view the client chips use
// (openCaseQuickView); "Full case →" in the modal opens the case.
// Locked (no access) cases render the show-but-lock treatment, never a link.
// ============================================================
import React, { useMemo, useState } from 'react'
import { LOCKED_CASE_TIP } from '../shared/CaseLink'
import FavouriteStar from '../shared/FavouriteStar'
import { ErrorNote } from '../shared/ActionBox'
import { LinkRelationshipBadge, LinkRelationshipControl } from '../cases/LinkRelationship'
import type { CaseLinkRelationship } from '../../types/case.types'

export type CaseStatusLabel = 'Open' | 'Draft' | 'Closed'
export interface CaseCardModel {
  id: string
  name: string           // shared caseName()
  ref: string            // shared caseRef()
  status: CaseStatusLabel
  accidentDate: string | null   // already formatted for display
  completion: number | null     // Setup % (calculateCaseCompletion)
  // Optional — rendered when the row carries it (the client-cases endpoint
  // doesn't return the fee earner yet; see report).
  assignee?: { name: string; colour: string } | null
  mine?: boolean
  // Show-but-lock: the viewer can't open this case.
  locked?: boolean
  // Favourite label — THE "<name> · <REF>" (caseNameWithRef), same as the caselist star.
  favLabel?: string
  /** (audit) false → hide the trash (server: assignee or admin only). */
  canDelete?: boolean
  /** (Batch 4) cases in this case's CONFIRMED same-accident group (incl.
   *  itself). ≥2 → the "Same accident" pill. Never set from a suggestion. */
  sameAccidentCount?: number
}
export interface LinkedCaseModel {
  id: string
  name: string
  ref: string
  clientName: string
  locked: boolean
  // The link's relationship — the shared badge + "Please confirm" choices
  // (LinkRelationshipControl), saved against fromCaseId ↔ id.
  fromCaseId: string
  relationship?: CaseLinkRelationship
  relationshipConfirmed?: boolean
}

interface ClientCasesTabProps {
  clientFirstName: string
  clientFullName?: string           // the add-case tile's "for <name>" line
  cases: CaseCardModel[] | null      // null = loading
  error: string | null
  linked: LinkedCaseModel[]
  onOpen: (caseId: string) => void   // → case quick-view
  openingId?: string | null
  onNewCase: () => void
  creating?: boolean
  // Case actions — the caselist's set, driven by the shared CaseActionsMenu in
  // the host (same menu, dialogs, permissions + delete flow).
  onMenu?: (caseId: string, x: number, y: number) => void   // ⋮ + right-click
  onDelete?: (caseId: string) => void                       // trash → same confirm
}

// ── mockup classes (exact values) ──
const CARD_SURFACE =
  'bg-casevo-surface border border-casevo-border rounded-[14px] ' +
  'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
// .cc — the compact forest card
const CC =
  'group relative isolate overflow-hidden rounded-[14px] text-white pt-[15px] pr-4 pb-0 pl-[18px] flex flex-col min-h-[142px] w-full text-left ' +
  'bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] shadow-[0_6px_18px_rgba(10,25,17,0.22)] ' +
  // HOVER — the card itself says "clickable" (no text hint): lifts 2px, the
  // shadow deepens and a faint inner hairline brightens the edge. Transform +
  // shadow only → no layout shift. Press settles it back down.
  'transition-[transform,box-shadow] duration-150 ease-out motion-reduce:transition-none ' +
  'dark:bg-[linear-gradient(135deg,#1b2e24,#223c2e)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(255,255,255,0.06)]'
// Hover (not while opening): the same lift + deeper shadow + brighter edge.
const CC_HOVER =
  'hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(10,25,17,0.34),inset_0_0_0_1px_rgba(255,255,255,0.14)] ' +
  'active:translate-y-0 motion-reduce:hover:translate-y-0 ' +
  'dark:hover:shadow-[0_6px_20px_rgba(0,0,0,0.45),inset_0_0_0_1px_rgba(255,255,255,0.16)]'
// Card-level icon actions (caselist: star / trash / ⋮), sized for the tile.
const ACT = 'w-7 h-7 rounded-[7px] grid place-items-center text-white/80 hover:text-white hover:bg-white/15 transition-colors focus:outline-none casevo-focus'
const CLOSED = 'saturate-[.55] dark:saturate-[.6] dark:brightness-[.92]'
const STP = 'shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-[9px] py-[3px] rounded-[4px] bg-white/20'
const YOU = 'shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#3a2e08] bg-casevo-gold px-2 py-[2px] rounded-[4px]'
const GH = 'flex items-center gap-[10px] text-[12px] font-bold uppercase tracking-[0.14em] mt-5 mx-[2px] mb-3 text-[#6f7d73] dark:text-[#7d9488]'
const GH_CNT = 'text-[11.5px] font-bold px-2 py-px rounded-full bg-casevo-border-soft text-casevo-text-soft tracking-normal'

// ── icons ──
const Svg: React.FC<{ s?: number; children: React.ReactNode; sw?: number }> = ({ s = 14, children, sw = 2 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">{children}</svg>
)
const IcCal: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Svg>
const IcUser: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>
const IcLock: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>
const IcPlus: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><path d="M12 5v14M5 12h14" /></Svg>
const IcCheck: React.FC<{ s?: number }> = ({ s }) => <Svg s={s} sw={3}><path d="M20 6 9 17l-5-5" /></Svg>
const IcLink: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></Svg>
const IcTrash: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Svg>
const IcDots: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></Svg>
const IcChevR: React.FC<{ s?: number }> = ({ s }) => <Svg s={s}><path d="M9 6l6 6-6 6" /></Svg>

// The shared locked-case copy (CaseLink.tsx) — same tooltip everywhere.
const LOCKED_TIP = LOCKED_CASE_TIP

// The card is a DIV (not a button) so it can hold real action buttons. A
// stretched, invisible <button> covers it and opens the quick-view (left-click
// / Enter); content sits above with pointer-events off so clicks fall through,
// except the action cluster (pointer-events on, own click handlers that stop
// propagation). Right-click anywhere → the shared case actions menu.
interface CardActions {
  onMenu?: (caseId: string, x: number, y: number) => void
  onDelete?: (caseId: string) => void
}
const CompactCard: React.FC<{ c: CaseCardModel; onOpen: (id: string) => void; busy: boolean } & CardActions> = ({ c, onOpen, busy, onMenu, onDelete }) => {
  const pct = c.completion
  return (
    <div
      className={`${CC} ${busy ? 'cursor-wait' : CC_HOVER} ${c.status === 'Closed' ? CLOSED : ''}`}
      onContextMenu={onMenu ? (e) => { e.preventDefault(); onMenu(c.id, e.clientX, e.clientY) } : undefined}
      aria-busy={busy || undefined}
    >
      <button
        type="button"
        onClick={() => onOpen(c.id)}
        disabled={busy}
        aria-label={`${c.name} — open quick view`}
        className="absolute inset-0 z-0 rounded-[14px] cursor-pointer disabled:cursor-wait focus:outline-none casevo-focus"
      />
      {c.mine && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 bg-casevo-gold pointer-events-none" />}
      <div className="relative z-[1] pointer-events-none flex items-center gap-2 min-w-0 w-full">
        <span className="flex-1 min-w-0 text-[16px] font-bold leading-[1.25] truncate">{c.name}</span>
        {c.mine && <span className={YOU}><IcCheck s={10} />You</span>}
        {(c.sameAccidentCount ?? 0) >= 2 && (
          <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-[9px] py-[3px] rounded-[4px] bg-white/20" title={`Confirmed: ${c.sameAccidentCount} cases from the same accident`}>Same accident</span>
        )}
        {/* Opening feedback — a small spinner beside the status, only while
            this card's quick-view is loading. */}
        {busy && <span aria-hidden="true" className="shrink-0 w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin motion-reduce:animate-none" />}
        <span className={STP}>{c.status}</span>
      </div>
      <div className="relative z-[1] pointer-events-none font-mono text-[11.5px] opacity-70 mt-[3px] truncate max-w-full">{c.ref}</div>
      <div className="relative z-[1] pointer-events-none w-full mt-[6px] flex items-center gap-3">
        <div className="flex-1 min-w-0 flex flex-wrap gap-x-[14px] gap-y-[3px] text-[12.5px] opacity-[.88]">
          <span className="inline-flex items-center gap-[5px]"><IcCal s={13} />{c.accidentDate ?? 'No accident date'}</span>
          {c.assignee !== undefined && (
            c.assignee
              ? (
                <span className="inline-flex items-center gap-[5px]">
                  <span aria-hidden="true" className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-white shadow-[0_0_0_1.5px_rgba(255,255,255,0.35)]" style={{ background: c.assignee.colour }}>
                    {c.assignee.name.trim().charAt(0).toUpperCase()}
                  </span>
                  {c.assignee.name}
                </span>
              )
              : <span className="inline-flex items-center gap-[5px] italic opacity-75"><IcUser s={13} />Unassigned</span>
          )}
        </div>
        {/* Case actions — the caselist's star / delete / ⋮ (pointer-events on). */}
        <div className="pointer-events-auto shrink-0 flex items-center gap-0.5 -mr-1.5" onKeyDown={(e) => e.stopPropagation()}>
          {c.favLabel && (
            <FavouriteStar itemType="case" itemId={c.id} label={c.favLabel} variant="dark" size={15} className="!p-1.5 !rounded-[7px] hover:bg-white/15" />
          )}
          {onDelete && c.canDelete !== false && (
            <button type="button" className={ACT} aria-label={`Delete ${c.name}`} title={`Delete ${c.name}`} onClick={(e) => { e.stopPropagation(); onDelete(c.id) }}>
              <IcTrash s={15} />
            </button>
          )}
          {onMenu && (
            <button
              type="button"
              className={ACT}
              aria-label={`Actions for ${c.name}`}
              title="Actions"
              onClick={(e) => {
                e.stopPropagation()
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                onMenu(c.id, r.left, r.bottom)
              }}
            >
              <IcDots s={15} />
            </button>
          )}
        </div>
      </div>
      <div className="relative z-[1] pointer-events-none mt-auto flex items-center gap-[10px] pt-[10px] pb-3 text-[11.5px] w-full">
        {pct !== null && (
          <>
            <span className="opacity-75">Setup</span>
            <span className="flex-1 h-[5px] rounded-[3px] bg-white/20 overflow-hidden">
              <i className="block h-full bg-[linear-gradient(90deg,#74c69d,#a8e6c5)]" style={{ width: `${pct}%` }} />
            </span>
            <b className="opacity-95 tabular-nums">{pct}%</b>
          </>
        )}
      </div>
    </div>
  )
}

// Show-but-lock (CaseLink LockIcon + LOCKED_CASE_TIP treatment): no name,
// never a link.
const LockedCard: React.FC<{ refText: string }> = ({ refText }) => (
  <div aria-disabled="true" title={LOCKED_TIP} className="min-h-[142px] flex flex-col items-start gap-2 pt-[15px] pb-[15px] px-[18px] rounded-[14px] border border-dashed border-casevo-border bg-transparent text-casevo-text-soft cursor-not-allowed select-none">
    <span className="w-[34px] h-[34px] rounded-[10px] bg-casevo-border-soft grid place-items-center shrink-0"><IcLock s={16} /></span>
    <div>
      <div className="font-semibold text-[14px]">Restricted case</div>
      <div className="text-[12.5px]">{LOCKED_TIP}</div>
    </div>
    {/* (Batch 4) restricted OWN cases arrive with no ref at all — nothing leaks. */}
    {refText && <span className="mt-auto font-mono text-[12px] opacity-80">{refText}</span>}
  </div>
)

// ── Add-case tile (mockup Option B .addtile) — closes the case grid in the
// next slot, same footprint as a case card (min-h 142, radius 14). Ghost/
// dashed so it never competes with real cases; lifts + the "+" turns on hover.
const AddCaseTile: React.FC<{ first: string; fullName?: string; onClick: () => void; creating?: boolean }> = ({ first, fullName, onClick, creating }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={creating}
    aria-label={`New case for ${first}`}
    className={
      'group min-h-[142px] w-full rounded-[14px] border-[1.5px] border-dashed border-[color-mix(in_srgb,var(--casevo-mid)_55%,var(--casevo-border))] bg-transparent ' +
      'text-casevo-brand dark:text-[#a8e6c5] flex flex-col items-center justify-center gap-2 ' +
      'transition-[background-color,border-color,transform] duration-150 ' +
      'enabled:hover:bg-[color-mix(in_srgb,var(--casevo-mint)_45%,transparent)] enabled:hover:border-casevo-mid enabled:hover:-translate-y-0.5 ' +
      'motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus:outline-none casevo-focus disabled:cursor-wait'
    }
  >
    <span className="w-10 h-10 rounded-[12px] bg-casevo-mint grid place-items-center transition-transform duration-150 group-enabled:group-hover:rotate-90 motion-reduce:transition-none">
      {creating
        ? <span aria-hidden="true" className="w-4 h-4 rounded-full border-2 border-[color-mix(in_srgb,currentColor_30%,transparent)] border-t-current animate-spin motion-reduce:animate-none" />
        : <IcPlus s={18} />}
    </span>
    <b className="text-[14.5px]">{creating ? 'Creating…' : 'New case'}</b>
    <small className="text-[12px] text-casevo-text-soft">for {fullName?.trim() || first}</small>
  </button>
)

const GroupHead: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <div className={GH}>{label}<span className={GH_CNT}>{count}</span><span className="flex-1 h-px bg-casevo-border-soft" /></div>
)

type Filter = 'all' | CaseStatusLabel

const ClientCasesTab: React.FC<ClientCasesTabProps> = ({ clientFirstName, clientFullName, cases, error, linked, onOpen, openingId, onNewCase, creating, onMenu, onDelete }) => {
  const first = clientFirstName.trim() || 'this client'
  const [filter, setFilter] = useState<Filter>('all')
  const counts = useMemo(() => {
    const o = { all: 0, Open: 0, Draft: 0, Closed: 0 }
    for (const c of cases ?? []) { o.all++; if (!c.locked) o[c.status]++ }
    return o
  }, [cases])
  const anyMine = (cases ?? []).some((c) => c.mine)

  // New case — ONE method: the add-case tile closing the grid (+ the empty
  // state's "Create the first case"). The header button was removed.
  const subtitle = cases === null
    ? 'Loading cases…'
    : cases.length === 0
      ? `${first} has no cases yet`
      : `${first}’s cases · ${counts.Open} open · ${counts.Draft} draft · ${counts.Closed} closed`

  const header = (
    <div className="flex items-end gap-3 mb-[14px] flex-wrap">
      <div className="flex-1 min-w-0">
        <h2 className="m-0 text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest">Cases</h2>
        <div className="text-[13px] text-casevo-text-soft mt-0.5">{subtitle}</div>
      </div>
    </div>
  )

  const errorBanner = error && <ErrorNote className="mb-[14px]">{error}</ErrorNote>

  // ── loading ──
  if (cases === null) {
    return (
      <div className="text-[14px] text-casevo-forest">
        {header}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5" aria-busy="true" aria-label="Loading cases">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="min-h-[142px] rounded-[14px] bg-casevo-border-soft animate-pulse motion-reduce:animate-none" />
          ))}
        </div>
      </div>
    )
  }

  // ── empty ──
  if (cases.length === 0 && linked.length === 0) {
    return (
      <div className="text-[14px] text-casevo-forest">
        {header}
        {errorBanner}
        {!error && (
          // Friendly empty state (mockup .emptyC): a little stack of forest
          // case cards with a gold "+", copy, and a larger create CTA.
          <div className="py-10 px-6 text-center rounded-[14px] border-[1.5px] border-dashed border-[color-mix(in_srgb,var(--casevo-mid)_45%,var(--casevo-border))] bg-casevo-surface">
            <div aria-hidden="true" className="relative w-[76px] h-[60px] mx-auto mb-[14px]">
              <i className="absolute left-1 top-[10px] w-14 h-10 rounded-[10px] bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] shadow-[0_6px_18px_rgba(10,25,17,0.2)] opacity-35 -rotate-[8deg]" />
              <i className="absolute left-4 top-1 w-14 h-10 rounded-[10px] bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] shadow-[0_6px_18px_rgba(10,25,17,0.2)]" />
              <span className="absolute right-0 bottom-0 w-[26px] h-[26px] rounded-full bg-casevo-gold text-[#3a2e08] grid place-items-center shadow-[0_0_0_3px_var(--casevo-surface)]"><IcPlus s={14} /></span>
            </div>
            <h3 className="m-0 mb-1 text-[17px] font-bold">No cases yet</h3>
            <p className="mx-auto mt-0 mb-[18px] max-w-[400px] text-[13.5px] text-casevo-text-soft">
              Create {first}’s first case — their details, address and GP carry across automatically.
            </p>
            <button
              type="button"
              onClick={onNewCase}
              disabled={creating}
              className="inline-flex items-center justify-center gap-2 h-10 pl-[14px] pr-[18px] rounded-[11px] bg-casevo-primary text-white text-[14px] font-semibold border-0 shadow-[0_6px_16px_rgba(45,106,79,0.28)] dark:shadow-[0_6px_16px_rgba(0,0,0,0.35)] transition-colors enabled:hover:bg-casevo-primary-hover focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-wait"
            >
              <IcPlus s={16} />{creating ? 'Creating…' : 'Create the first case'}
            </button>
          </div>
        )}
      </div>
    )
  }

  const matches = (c: CaseCardModel): boolean => filter === 'all' || (!c.locked && c.status === filter)
  const active = cases.filter((c) => (c.locked || c.status !== 'Closed') && matches(c))
  const closed = cases.filter((c) => !c.locked && c.status === 'Closed' && matches(c))
  const SEGS: { key: Filter; label: string; n: number }[] = [
    { key: 'all', label: 'All', n: counts.all },
    { key: 'Open', label: 'Open', n: counts.Open },
    { key: 'Draft', label: 'Draft', n: counts.Draft },
    { key: 'Closed', label: 'Closed', n: counts.Closed },
  ]
  const card = (c: CaseCardModel): React.ReactNode =>
    c.locked
      ? <LockedCard key={c.id} refText={c.ref} />
      : <CompactCard key={c.id} c={c} onOpen={onOpen} busy={openingId === c.id} onMenu={onMenu} onDelete={onDelete} />

  return (
    <div className="text-[14px] text-casevo-forest">
      {header}
      {errorBanner}
      {cases.length > 0 && (
        <div className="flex items-center gap-[10px] mb-4 flex-wrap">
          <div role="group" aria-label="Filter cases by status" className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface">
            {SEGS.map((s) => {
              const on = filter === s.key
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFilter(s.key)}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 text-[13px] border-r border-casevo-border last:border-r-0 focus:outline-none casevo-focus ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`}
                >
                  {s.label} <small className="text-[11.5px] opacity-80">{s.n}</small>
                </button>
              )
            })}
          </div>
          {anyMine && (
            <span className="ml-auto text-[12.5px] text-casevo-text-soft inline-flex items-center gap-1.5"><IcCheck s={12} />Gold bar = you’re on the team</span>
          )}
        </div>
      )}

      {/* Active grid — the add-case tile closes it in the next slot (new
          cases are active). Hidden only under the Closed filter. */}
      {(active.length > 0 || filter !== 'Closed') && (
        <>
          <GroupHead label="Active" count={active.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {active.map(card)}
            {filter !== 'Closed' && <AddCaseTile first={first} fullName={clientFullName} onClick={onNewCase} creating={creating} />}
          </div>
        </>
      )}
      {closed.length > 0 && (
        <>
          <GroupHead label="Closed" count={closed.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{closed.map(card)}</div>
        </>
      )}
      {cases.length > 0 && active.length === 0 && closed.length === 0 && (
        <p className="mt-5 mx-[2px] text-[13px] text-casevo-text-soft">No {filter.toLowerCase()} cases.</p>
      )}

      {/* Linked to — other clients' cases this client's cases are linked to. */}
      {filter === 'all' && linked.length > 0 && (
        <>
          <GroupHead label="Linked to" count={linked.length} />
          {linked.map((l) => l.locked ? (
            <div key={l.id} aria-disabled="true" title={LOCKED_TIP} className="flex items-center gap-3 px-[14px] py-3 mb-2 rounded-[14px] border border-dashed border-casevo-border bg-transparent cursor-not-allowed select-none">
              <span className="w-8 h-8 rounded-[9px] bg-casevo-border-soft text-casevo-text-soft grid place-items-center shrink-0"><IcLock s={15} /></span>
              <div className="min-w-0">
                <div className="font-semibold text-[14px] text-casevo-text-soft">Restricted case</div>
                <div className="text-[12.5px] text-casevo-text-soft truncate">{LOCKED_TIP} · <span className="font-mono">{l.ref}</span></div>
                {/* The relationship stays visible (it's this client's link);
                    no choices — the other case can't be opened. */}
                <div className="mt-1.5"><LinkRelationshipBadge relationship={l.relationship} confirmed={l.relationshipConfirmed} /></div>
              </div>
            </div>
          ) : (
            // A DIV (it holds the relationship buttons): a stretched invisible
            // button opens the quick view; content lets clicks fall through
            // except the relationship control (pointer-events on).
            <div
              key={l.id}
              className={`${CARD_SURFACE} relative w-full flex items-center gap-3 px-[14px] py-3 mb-2 hover:border-casevo-mid transition-colors ${openingId === l.id ? 'cursor-wait' : ''}`}
            >
              <button
                type="button"
                onClick={() => onOpen(l.id)}
                disabled={openingId === l.id}
                aria-label={`${l.name} — open quick view`}
                className="absolute inset-0 z-0 rounded-[14px] cursor-pointer disabled:cursor-wait focus:outline-none casevo-focus"
              />
              <span className="relative z-[1] pointer-events-none w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0"><IcLink s={15} /></span>
              <div className="relative z-[1] pointer-events-none min-w-0 flex-1">
                <div className="font-semibold text-[14px] truncate">{l.name}</div>
                <div className="text-[12.5px] text-casevo-text-soft truncate">Client: {l.clientName} · <span className="font-mono">{l.ref}</span></div>
                <div className="mt-1.5 pointer-events-auto w-fit max-w-full">
                  <LinkRelationshipControl caseId={l.fromCaseId} link={{ caseId: l.id, relationship: l.relationship, relationshipConfirmed: l.relationshipConfirmed }} />
                </div>
              </div>
              <span className="relative z-[1] pointer-events-none ml-auto text-casevo-text-soft">{openingId === l.id ? <span className="text-[12px]">Opening…</span> : <IcChevR s={16} />}</span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default ClientCasesTab
